<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="mt-4 ml-4">
        <v-col>
          <span
            class="text-h5 font-weight-bold"
            v-text="'User Shift Assignment'"
          ></span>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-text>
              <v-row align="center">
                <v-col md="3">
                  <v-text-field
                    v-model="search_keyword"
                    label="Search user..."
                    prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details clearable
                    @keydown.enter="getData"
                    @change="page = 1"
                  ></v-text-field>
                </v-col>
                <v-col md="3" v-if="allCompanies.length > 0">
                  <v-autocomplete
                    v-model="filter_company_id"
                    :items="allCompanies"
                    label="Company"
                    prepend-inner-icon="mdi-domain"
                    outlined dense hide-details clearable
                    @input="onCompanyChange"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-autocomplete
                    v-model="filter_shift_id"
                    :items="shifts"
                    label="Filter by Shift"
                    outlined dense hide-details clearable
                    prepend-inner-icon="mdi-clock-outline"
                    @input="getData"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-checkbox
                    v-model="without_shift"
                    label="Without Shift"
                    hide-details
                    @change="getData"
                  ></v-checkbox>
                </v-col>
                <v-spacer></v-spacer>
                <v-col md="2">
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                  ></v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Bulk Actions -->
      <v-row v-if="selectedUsers.length > 0">
        <v-col md="12">
          <v-card elevation="6" color="blue lighten-5">
            <v-card-text>
              <v-row align="center">
                <v-col md="6">
                  <v-autocomplete
                    v-model="bulk_shift_id"
                    :items="shifts"
                    label="Select Shift for Bulk Assignment"
                    prepend-icon="mdi-clock-outline"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="6">
                  <v-btn
                    color="primary"
                    @click="assignBulkShifts"
                    :disabled="!bulk_shift_id"
                    :loading="isBulkLoading"
                  >
                    Assign Shift to {{ selectedUsers.length }} Users
                  </v-btn>
                  <v-btn
                    color="error"
                    class="ml-2"
                    @click="removeBulkShifts"
                    :disabled="!bulk_shift_id"
                    :loading="isBulkLoading"
                  >
                    Remove Shift from {{ selectedUsers.length }} Users
                  </v-btn>
                  <v-btn text class="ml-2" @click="selectedUsers = []">
                    Clear Selection
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-row v-else>
        <v-col md="12">
          <v-card elevation="6">
            <v-simple-table fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      <v-checkbox
                        v-model="selectAll"
                        @change="toggleSelectAll"
                      ></v-checkbox>
                    </th>
                    <th class="text-left">Sr No</th>
                    <th class="text-left">Name</th>
                    <th class="text-left">Employee Code</th>
                    <th class="text-left">Email</th>
                    <th class="text-left">Designation</th>
                    <th class="text-left">Current Shift</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody v-if="users.length != 0">
                  <tr v-for="(user, i) in users" :key="`user${i}`">
                    <td>
                      <v-checkbox
                        v-model="selectedUsers"
                        :value="user.id"
                      ></v-checkbox>
                    </td>
                    <td>{{ i + 1 }}</td>
                    <td>{{ user.first_name }} {{ user.last_name }}</td>
                    <td>{{ user.employee_code }}</td>
                    <td>{{ user.email }}</td>
                    <td>
                      {{ user.designation ? user.designation.name : "N/A" }}
                    </td>
                    <td>
                      <div v-if="user.shifts && user.shifts.length > 0">
                        <v-chip
                          v-for="(shift, idx) in user.shifts"
                          :key="`shift-${idx}`"
                          small
                          :color="shift.pivot.is_primary ? 'primary' : 'secondary'"
                          class="ma-1"
                        >
                          {{ shift.shift_name }}
                          <v-icon
                            v-if="shift.pivot.is_primary"
                            small
                            right
                            color="white"
                            >mdi-star</v-icon
                          >
                        </v-chip>
                      </div>
                      <v-chip v-else-if="user.shift" small color="primary">
                        {{ user.shift.shift_name }}
                      </v-chip>
                      <span v-else class="grey--text">No Shift Assigned</span>
                    </td>
                    <td class="text-right">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            @click="openAssignDialog(user)"
                          >
                            <v-icon small>mdi-clock-plus</v-icon>
                          </v-btn>
                        </template>
                        <span>Assign Shift</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        v-if="
                          (user.shifts && user.shifts.length > 0) || user.shift_id
                        "
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            @click="openRemoveDialog(user)"
                          >
                            <v-icon small>mdi-clock-remove</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove Shift(s)</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        v-if="user.shifts && user.shifts.length > 0"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            @click="viewUserShifts(user)"
                          >
                            <v-icon small>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>View All Shifts</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="8" class="text-center">No users found</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Assign Shift Dialog -->
      <v-dialog v-model="assignDialog" max-width="600">
        <v-card>
          <v-card-title>
            Assign Shift to {{ selectedUser.first_name }}
            {{ selectedUser.last_name }}
          </v-card-title>
          <v-card-text>
            <v-form ref="assignForm">
              <v-autocomplete
                v-model="assign_shift_id"
                :items="availableShifts"
                label="Select Shift *"
                prepend-icon="mdi-clock-outline"
                :rules="[rules.required]"
              ></v-autocomplete>

              <v-checkbox
                v-model="assign_is_primary"
                label="Set as Primary Shift"
                hint="Primary shift will be used for attendance calculation"
                persistent-hint
              ></v-checkbox>

              <v-autocomplete
                v-model="assign_applicable_days"
                :items="weekDays"
                item-text="name"
                item-value="id"
                label="Applicable Days (Optional)"
                prepend-icon="mdi-calendar-week"
                multiple
                chips
                hint="Leave empty for all days"
                persistent-hint
              ></v-autocomplete>

              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="assign_start_date"
                    label="Start Date (Optional)"
                    type="date"
                    prepend-icon="mdi-calendar-start"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="assign_end_date"
                    label="End Date (Optional)"
                    type="date"
                    prepend-icon="mdi-calendar-end"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="assignDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="assignShift"
              :loading="isAssignLoading"
              :disabled="!assign_shift_id"
            >
              Assign Shift
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Remove Shift Dialog -->
      <v-dialog v-model="removeDialog" max-width="500">
        <v-card>
          <v-card-title>
            Remove Shift from {{ selectedUser.first_name }}
            {{ selectedUser.last_name }}
          </v-card-title>
          <v-card-text>
            <v-select
              v-model="remove_shift_id"
              :items="userShiftsForRemoval"
              label="Select Shift to Remove"
              prepend-icon="mdi-clock-outline"
              hint="Select a specific shift or remove all"
              persistent-hint
            ></v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="removeDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="removeShift"
              :loading="isRemoveLoading"
              :disabled="!remove_shift_id"
            >
              Remove
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- View User Shifts Dialog -->
      <v-dialog v-model="viewShiftsDialog" max-width="700">
        <v-card>
          <v-card-title>
            Shifts for {{ selectedUser.first_name }}
            {{ selectedUser.last_name }}
          </v-card-title>
          <v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Shift Name</th>
                    <th>Primary</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Applicable Days</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(shift, i) in selectedUser.shifts"
                    :key="`view-shift-${i}`"
                  >
                    <td>{{ shift.shift_name }}</td>
                    <td>
                      <v-icon v-if="shift.pivot.is_primary" color="primary"
                        >mdi-check</v-icon
                      >
                    </td>
                    <td>
                      <v-chip
                        small
                        :color="shift.pivot.is_active ? 'green' : 'red'"
                      >
                        {{ shift.pivot.is_active ? "Active" : "Inactive" }}
                      </v-chip>
                    </td>
                    <td>{{ shift.pivot.start_date || "N/A" }}</td>
                    <td>{{ shift.pivot.end_date || "N/A" }}</td>
                    <td>
                      {{
                        shift.pivot.applicable_days
                          ? getApplicableDaysText(shift.pivot.applicable_days)
                          : "All Days"
                      }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="viewShiftsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      search_keyword: "",
      filter_shift_id: null,
      without_shift: false,
      page: 1,
      paginationLength: 1,
      users: [],
      shifts: [],
      weekDays: [],
      isLoading: true,
      assignDialog: false,
      removeDialog: false,
      viewShiftsDialog: false,
      selectedUser: {},
      assign_shift_id: null,
      assign_is_primary: false,
      assign_applicable_days: [],
      assign_start_date: null,
      assign_end_date: null,
      remove_shift_id: null,
      isAssignLoading: false,
      isRemoveLoading: false,
      selectedUsers: [],
      selectAll: false,
      bulk_shift_id: null,
      isBulkLoading: false,
      filter_company_id: "all",
      allCompanies: [],
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    availableShifts() {
      // Filter out shifts that are already assigned to the user
      if (!this.selectedUser.shifts) return this.shifts;
      const assignedShiftIds = this.selectedUser.shifts.map((s) => s.id);
      return this.shifts.filter((s) => !assignedShiftIds.includes(s.value));
    },
    userShiftsForRemoval() {
      if (!this.selectedUser.shifts || this.selectedUser.shifts.length === 0) {
        return [];
      }
      const options = this.selectedUser.shifts.map((shift) => ({
        text: shift.shift_name,
        value: shift.id,
      }));
      // Add option to remove all shifts
      if (this.selectedUser.shifts.length > 1) {
        options.unshift({
          text: "Remove All Shifts",
          value: "all",
        });
      }
      return options;
    },
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  watch: {
    page() {
      this.getData();
    },
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/user_shifts/masters", {
          params: {
            filter_company_id: this.filter_company_id === "all" ? "" : this.filter_company_id || "",
          },
        });
        if (response.data.success) {
          this.shifts = response.data.shifts.map((shift) => ({
            text: shift.shift_name,
            value: shift.id,
          }));
          this.weekDays = response.data.weekDays || [];
          if (response.data.companies && response.data.companies.length > 0) {
            this.allCompanies = [{ text: "All Companies", value: "all" }];
            response.data.companies.forEach((c) => {
              this.allCompanies.push({ text: c.name, value: c.id });
            });
          }
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    async onCompanyChange() {
      // When the company filter changes, reload shifts (which are company-specific)
      // and reset the shift filter since old shift IDs may not exist in the new company.
      this.filter_shift_id = null;
      await this.getMasters();
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      this.selectedUsers = [];
      this.selectAll = false;
      try {
        const response = await axios.get("/user_shifts", {
          params: {
            page: this.page,
            rowsPerPage: this.rowsPerPage,
            search_keyword: this.search_keyword,
            shift_id: this.filter_shift_id,
            without_shift: this.without_shift ? 1 : 0,
            filter_company_id: this.filter_company_id || "",
          },
        });
        this.users = response.data.data;
        this.paginationLength = Math.ceil(
          response.data.count / this.rowsPerPage
        );
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openAssignDialog(user) {
      this.selectedUser = { ...user };
      this.assign_shift_id = null;
      this.assign_is_primary = false;
      this.assign_applicable_days = [];
      this.assign_start_date = null;
      this.assign_end_date = null;
      this.assignDialog = true;
    },
    async assignShift() {
      this.isAssignLoading = true;
      try {
        const payload = {
          user_id: this.selectedUser.id,
          shift_id: this.assign_shift_id,
          is_primary: this.assign_is_primary,
          is_active: true,
        };

        if (this.assign_start_date) {
          payload.start_date = this.assign_start_date;
        }
        if (this.assign_end_date) {
          payload.end_date = this.assign_end_date;
        }
        if (this.assign_applicable_days.length > 0) {
          payload.applicable_days = this.assign_applicable_days;
        }

        const response = await axios.post("/user_shifts", payload);
        if (response.data.success) {
          this.assignDialog = false;
          this.getData();
        }
      } catch (error) {
        console.error("Error assigning shift:", error);
      } finally {
        this.isAssignLoading = false;
      }
    },
    openRemoveDialog(user) {
      this.selectedUser = { ...user };
      this.remove_shift_id = null;
      this.removeDialog = true;
    },
    async removeShift() {
      this.isRemoveLoading = true;
      try {
        let response;
        if (this.remove_shift_id === "all") {
          // Remove all shifts
          response = await axios.post("/user_shifts/remove_all_shifts", {
            user_id: this.selectedUser.id,
          });
        } else {
          // Remove specific shift
          response = await axios.post("/user_shifts/remove_shift", {
            user_id: this.selectedUser.id,
            shift_id: this.remove_shift_id,
          });
        }
        if (response.data.success) {
          this.removeDialog = false;
          this.getData();
        }
      } catch (error) {
        console.error("Error removing shift:", error);
      } finally {
        this.isRemoveLoading = false;
      }
    },
    viewUserShifts(user) {
      this.selectedUser = { ...user };
      this.viewShiftsDialog = true;
    },
    getApplicableDaysText(applicableDaysJson) {
      try {
        const days =
          typeof applicableDaysJson === "string"
            ? JSON.parse(applicableDaysJson)
            : applicableDaysJson;
        if (!days || days.length === 0) return "All Days";
        const dayNames = days
          .map((dayId) => {
            const day = this.weekDays.find((d) => d.id === dayId);
            return day ? day.name : "";
          })
          .filter((name) => name);
        return dayNames.join(", ");
      } catch (e) {
        return "All Days";
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedUsers = this.users.map((u) => u.id);
      } else {
        this.selectedUsers = [];
      }
    },
    async assignBulkShifts() {
      if (!this.bulk_shift_id) return;
      this.isBulkLoading = true;
      try {
        const response = await axios.post("/user_shifts/assign_multiple", {
          user_ids: this.selectedUsers,
          shift_id: this.bulk_shift_id,
        });
        if (response.data.success) {
          this.selectedUsers = [];
          this.bulk_shift_id = null;
          this.getData();
        }
      } catch (error) {
        console.error("Error assigning bulk shifts:", error);
      } finally {
        this.isBulkLoading = false;
      }
    },
    async removeBulkShifts() {
      if (!this.bulk_shift_id) return;
      if (
        confirm(
          `Are you sure you want to remove the selected shift from ${this.selectedUsers.length} users?`
        )
      ) {
        this.isBulkLoading = true;
        try {
          const response = await axios.post("/user_shifts/remove_multiple", {
            user_ids: this.selectedUsers,
            shift_id: this.bulk_shift_id,
          });
          if (response.data.success) {
            this.selectedUsers = [];
            this.bulk_shift_id = null;
            this.getData();
          }
        } catch (error) {
          console.error("Error removing bulk shifts:", error);
        } finally {
          this.isBulkLoading = false;
        }
      }
    },
  },
};
</script>
